// Side-panel row editor (insert or update). Reads + writes
// `root._extEditor`. Save/delete flow through saveRemoteRow /
// deleteRemoteRow. Opens when `_extEditor` is non-null.

export const RemoteRowField = {
  flow: 'y',
  gap: 'V',

  Label: {
    tag: 'label',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '700',
    text: (el, s) => {
      const col = s.col || {}
      const parts = [col.name]
      if (col.is_primary_key) parts.push('(PK)')
      if (!col.nullable) parts.push('*')
      return parts.join(' ')
    }
  },

  TypeHint: {
    tag: 'span',
    fontSize: 'Y1',
    color: 'caption',
    text: (el, s) => {
      const col = s.col || {}
      return col.format ? `${col.type} · ${col.format}` : col.type || ''
    }
  },

  Input: {
    hide: (el, s) => {
      const col = s.col || {}
      return col.type === 'boolean' || col.format === 'jsonb' || col.format === 'json'
    },
    tag: 'input',
    type: 'text',
    width: '100%',
    padding: 'Z',
    round: 'Y',
    background: 'document',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    color: 'title',
    fontFamily: 'mono',
    fontSize: 'Z2',
    disabled: (el, s) => {
      const ed = s.root?._extEditor
      const col = s.col || {}
      return ed?.mode === 'update' && col.is_primary_key
    },
    placeholder: (el, s) => {
      const col = s.col || {}
      if (col.format === 'uuid') return '00000000-0000-0000-0000-000000000000'
      if (col.format === 'date-time') return '2026-01-01T00:00:00Z'
      return col.nullable ? '(null)' : ''
    },
    value: (el, s) => {
      const ed = s.root?._extEditor
      return ed?.values?.[s.fieldKey] ?? ''
    },
    onInput: (e, el, s) => {
      const ed = s.root?._extEditor
      if (!ed) return
      s.root.update({
        _extEditor: {
          ...ed,
          values: { ...(ed.values || {}), [s.fieldKey]: e.target.value }
        }
      })
    }
  },

  BoolSelect: {
    hide: (el, s) => s.col?.type !== 'boolean',
    tag: 'select',
    width: '100%',
    padding: 'Z',
    round: 'Y',
    background: 'document',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    color: 'title',
    fontSize: 'Z2',
    value: (el, s) => {
      const ed = s.root?._extEditor
      const v = ed?.values?.[s.fieldKey]
      if (v === true || v === 'true') return 'true'
      if (v === false || v === 'false') return 'false'
      return ''
    },
    onChange: (e, el, s) => {
      const ed = s.root?._extEditor
      if (!ed) return
      s.root.update({
        _extEditor: {
          ...ed,
          values: { ...(ed.values || {}), [s.fieldKey]: e.target.value }
        }
      })
    },
    OptEmpty: { tag: 'option', value: '', text: '—' },
    OptTrue: { tag: 'option', value: 'true', text: 'true' },
    OptFalse: { tag: 'option', value: 'false', text: 'false' }
  },

  JsonArea: {
    hide: (el, s) => {
      const col = s.col || {}
      return col.format !== 'jsonb' && col.format !== 'json'
    },
    tag: 'textarea',
    rows: '4',
    width: '100%',
    padding: 'Z',
    round: 'Y',
    background: 'document',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    color: 'title',
    fontFamily: 'mono',
    fontSize: 'Y2',
    placeholder: '{} or []',
    value: (el, s) => {
      const ed = s.root?._extEditor
      return ed?.values?.[s.fieldKey] ?? ''
    },
    onInput: (e, el, s) => {
      const ed = s.root?._extEditor
      if (!ed) return
      s.root.update({
        _extEditor: {
          ...ed,
          values: { ...(ed.values || {}), [s.fieldKey]: e.target.value }
        }
      })
    }
  }
}

export const RemoteRowEditor = {
  hide: (el, s) => !s.root?._extEditor,
  position: 'fixed',
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  background: 'rgba(0,0,0,0.4)',
  zIndex: '100',
  align: 'flex-start flex-end',
  onClick: (e, el, s) => {
    if (e.target === el.node) s.root.update({ _extEditor: null })
  },

  Panel: {
    width: '520px',
    height: '100%',
    background: 'document',
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'line',
    overflow: 'auto',
    flow: 'y',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',

    Header: {
      flow: 'x',
      align: 'center space-between',
      padding: 'A',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      Title: {
        flow: 'y',
        gap: 'V',
        H2: {
          tag: 'h2',
          fontSize: 'B',
          fontWeight: '700',
          color: 'title',
          margin: '0',
          text: (el, s) => (s.root?._extEditor?.mode === 'insert' ? 'New row' : 'Edit row')
        },
        Meta: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: (el, s) => {
            const ed = s.root?._extEditor
            if (!ed) return ''
            return `${ed.slug} · ${ed.table}`
          }
        }
      },
      CloseBtn: {
        tag: 'button',
        type: 'button',
        padding: 'V Z',
        round: 'Y',
        background: 'transparent',
        border: 'none',
        color: 'caption',
        fontSize: 'A',
        cursor: 'pointer',
        text: '×',
        onClick: (e, el, s) => s.root.update({ _extEditor: null })
      }
    },

    Body: {
      padding: 'A',
      flow: 'y',
      gap: 'Z1',
      Fields: {
        flow: 'y',
        gap: 'Z1',
        childExtends: 'RemoteRowField',
        childrenAs: 'state',
        children: (el, s) => {
          const ed = s.root?._extEditor
          if (!ed) return []
          const schema = el.call('schemaForRemoteTable', s.root, ed.slug, ed.table)
          return (schema?.columns || []).map((c) => ({ col: c, fieldKey: c.name }))
        }
      }
    },

    Footer: {
      flow: 'x',
      align: 'center space-between',
      padding: 'A',
      gap: 'Z',
      borderTopWidth: '1px',
      borderTopStyle: 'solid',
      borderTopColor: 'line',
      background: 'overlay',
      position: 'sticky',
      bottom: '0',

      DeleteBtn: {
        tag: 'button',
        type: 'button',
        hide: (el, s) => s.root?._extEditor?.mode !== 'update',
        padding: 'V Z',
        round: 'Y',
        background: 'transparent',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        color: 'red',
        fontSize: 'Y2',
        cursor: 'pointer',
        text: 'Delete row',
        onClick: async (e, el, s) => {
          if (!window.confirm('Delete this row? This cannot be undone.')) return
          const res = await el.call('deleteRemoteRow', s.root)
          if (res?.ok) {
            el.call('openNotification', { title: 'Row deleted', type: 'success' })
          } else {
            el.call('openNotification', { title: 'Delete failed', message: res?.error, type: 'error' })
          }
        }
      },

      Right: {
        flow: 'x',
        gap: 'Z',
        CancelBtn: {
          tag: 'button',
          type: 'button',
          padding: 'V Z',
          round: 'Y',
          background: 'transparent',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'line',
          color: 'title',
          fontSize: 'Y2',
          cursor: 'pointer',
          text: 'Cancel',
          onClick: (e, el, s) => s.root.update({ _extEditor: null })
        },
        SaveBtn: {
          tag: 'button',
          type: 'button',
          padding: 'V A',
          round: 'Y',
          background: 'blue',
          color: 'white',
          fontSize: 'Y2',
          fontWeight: '700',
          cursor: 'pointer',
          border: 'none',
          text: (el, s) => (s.root?._extEditorSaving ? 'Saving…' : 'Save'),
          onClick: async (e, el, s) => {
            const res = await el.call('saveRemoteRow', s.root)
            if (res?.ok) {
              el.call('openNotification', { title: 'Saved', type: 'success' })
            } else {
              el.call('openNotification', { title: 'Save failed', message: res?.error, type: 'error' })
            }
          }
        }
      }
    }
  }
}
